import React, { useEffect, useState } from "react";
import {
    collection,
    addDoc,
    updateDoc,
    doc,
    onSnapshot,
    serverTimestamp,
} from "firebase/firestore";
import db from "./Firebase";
import moment from "moment";

function MentorshipSessionManager() {
    const initial = {
        name: "",
        description: "",
        funder: "",
        image: "",
        join_link: "",
        seats: "",
        status: "",
        start_date_time: "",
        end_date_time: "",
    };

    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        const unsub = onSnapshot(
            collection(db, "upcoming_sessions_mentorship"),
            (snap) => {
                setSessions(
                    snap.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    }))
                );
            }
        );
        return () => unsub();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const resetForm = () => {
        setForm(initial);
        setEditId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.start_date_time || !form.end_date_time) {
            alert("Please fill required fields");
            return;
        }
        setSubmitting(true);
        const payload = {
            ...form,
            start_date_time: new Date(form.start_date_time),
            end_date_time: new Date(form.end_date_time),
            registered_uid: [],
            updated_at: serverTimestamp(),
        };
        try {
            if (editId) {
                await updateDoc(doc(db, "upcoming_sessions_mentorship", editId), payload);
                alert("Session updated successfully");
            } else {
                await addDoc(collection(db, "upcoming_sessions_mentorship"), {
                    ...payload,
                    registered_uid: [],
                    created_at: serverTimestamp(),
                });
                alert("Session created successfully");
            }
            resetForm();
        } catch (err) {
            console.error(err);
            alert("Error saving session");
        }
        setSubmitting(false);
    };

    const handleEdit = (session) => {
        setForm({
            ...session,
            start_date_time: formatForInput(session.start_date_time),
            end_date_time: formatForInput(session.end_date_time),
        });
        setEditId(session.id);
    };

    const formatForInput = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp?.seconds
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp);
        return date.toISOString().slice(0, 16);
    };

    return (
        <div className="msm-container">
            <h2 className="msm-title">Mentorship Sessions</h2>

            <div className="msm-table-container">
                <table className="msm-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>Funder</th>
                            <th>Status</th>
                            <th>Edit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sessions.length > 0 ? (
                            sessions.map((session) => (
                                <tr key={session.id}>
                                    <td>{session.name}</td>
                                    <td>
                                        {session.start_date_time
                                            ? moment(session.start_date_time.seconds * 1000).format("DD/MM/YYYY HH:mm")
                                            : ""}
                                    </td>
                                    <td>
                                        {session.end_date_time
                                            ? moment(session.end_date_time.seconds * 1000).format("DD/MM/YYYY HH:mm")
                                            : ""}
                                    </td>
                                    <td>{session.funder}</td>
                                    <td>{session.status}</td>
                                    <td>
                                        <button
                                            className="msm-btn-edit"
                                            onClick={() => handleEdit(session)}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="msm-no-data">No sessions found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="msm-form-wrapper">
                <h3 className="msm-form-title">
                    {editId ? "Edit Session" : "Add Session"}
                </h3>
                <form className="msm-form" onSubmit={handleSubmit}>
                    <div className="msm-form-row">
                        <div className="msm-form-group">
                            <label>Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="msm-form-group">
                            <label>Funder</label>
                            <input
                                type="text"
                                name="funder"
                                placeholder="e.g., nascom"
                                value={form.funder}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="msm-form-group">
                        <label>Description</label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                        ></textarea>
                    </div>

                    <div className="msm-form-row">
                        <div className="msm-form-group">
                            <label>Image URL</label>
                            <input
                                type="url"
                                name="image"
                                value={form.image}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="msm-form-group">
                            <label>Join Link</label>
                            <input
                                type="url"
                                name="join_link"
                                value={form.join_link}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="msm-form-row">
                        <div className="msm-form-group">
                            <label>Seats</label>
                            <input
                                type="number"
                                name="seats"
                                value={form.seats}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="msm-form-group">
                            <label>Status</label>
                            <select
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                            >
                                <option value="">Select</option>
                                <option value="1">1 (Active)</option>
                                <option value="0">0 (Inactive)</option>
                            </select>
                        </div>
                    </div>

                    <div className="msm-form-row">
                        <div className="msm-form-group">
                            <label>Start Date & Time *</label>
                            <input
                                type="datetime-local"
                                name="start_date_time"
                                value={form.start_date_time}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="msm-form-group">
                            <label>End Date & Time *</label>
                            <input
                                type="datetime-local"
                                name="end_date_time"
                                value={form.end_date_time}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="msm-form-actions">
                        <button type="submit" disabled={submitting} className="msm-btn-save">
                            {submitting ? "Saving..." : editId ? "Update" : "Create"}
                        </button>
                        {editId && (
                            <button
                                type="button"
                                className="msm-btn-cancel"
                                onClick={resetForm}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default MentorshipSessionManager;
